import { useEffect, useRef } from 'react'

function smoothScrollTo(targetY: number, duration = 900) {
  const startY = window.scrollY
  const distance = targetY - startY
  const startTime = performance.now()

  const easeInOutQuad = (t: number) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2)

  function step(now: number) {
    const elapsed = now - startTime
    const progress = Math.min(elapsed / duration, 1)
    window.scrollTo(0, startY + distance * easeInOutQuad(progress))
    if (progress < 1) requestAnimationFrame(step)
  }
  requestAnimationFrame(step)
}

/**
 * Auto-scrolls through every `[data-kiosk-section]` element inside
 * `containerRef`, pausing `dwellMs` on each, then calls `onCycleEnd`
 * once past the last one and loops back to index 0 — so it naturally
 * keeps working across a tab switch instead of needing to be restarted.
 *
 * Pauses while the user hovers the container, or right after they
 * manually scroll/touch it, and resumes on its own once they stop
 * interacting for `resumeDelayMs`.
 */
export function useKioskAutoplay(
  containerRef: React.RefObject<HTMLElement>,
  enabled: boolean,
  dwellMs: number,
  onCycleEnd: () => void,
  resumeDelayMs = 5000,
) {
  const indexRef = useRef(0)
  const pausedRef = useRef(false)
  const resumeTimeoutRef = useRef<number>()

  useEffect(() => {
    if (!enabled) {
      pausedRef.current = false
      indexRef.current = 0
      return
    }
    indexRef.current = 0
    pausedRef.current = false

    const pauseTemporarily = () => {
      pausedRef.current = true
      window.clearTimeout(resumeTimeoutRef.current)
      resumeTimeoutRef.current = window.setTimeout(() => {
        pausedRef.current = false
      }, resumeDelayMs)
    }
    const pauseWhileHovering = () => { pausedRef.current = true }
    const resumeOnLeave = () => { pausedRef.current = false }

    const container = containerRef.current
    container?.addEventListener('wheel', pauseTemporarily, { passive: true })
    container?.addEventListener('touchstart', pauseTemporarily, { passive: true })
    container?.addEventListener('mouseenter', pauseWhileHovering)
    container?.addEventListener('mouseleave', resumeOnLeave)

    const intervalId = window.setInterval(() => {
      if (pausedRef.current) return
      const c = containerRef.current
      if (!c) return
      const sections = Array.from(c.querySelectorAll<HTMLElement>('[data-kiosk-section]'))
      if (!sections.length) return

      if (indexRef.current >= sections.length) {
        indexRef.current = 0
        onCycleEnd()
        return
      }
      const el = sections[indexRef.current]
      const targetY = el.getBoundingClientRect().top + window.scrollY - 24
      smoothScrollTo(targetY, 900)
      indexRef.current += 1
    }, dwellMs)

    return () => {
      window.clearInterval(intervalId)
      window.clearTimeout(resumeTimeoutRef.current)
      container?.removeEventListener('wheel', pauseTemporarily)
      container?.removeEventListener('touchstart', pauseTemporarily)
      container?.removeEventListener('mouseenter', pauseWhileHovering)
      container?.removeEventListener('mouseleave', resumeOnLeave)
    }
  }, [enabled, dwellMs, onCycleEnd, containerRef, resumeDelayMs])
}